/* global globalThis */
import { isPublicFormContentItem, normalizePublicFormDraft } from './publicFormBuilder';

// Presentation allowlist: no record IDs, mappings, recipients, link secrets,
// defaults or prefilled client answers cross into the opaque-origin frame.
export const buildPublicFormPreviewSchema = (value) => {
  const draft = normalizePublicFormDraft(value);
  const fieldIds = new Map();
  draft.fields.forEach((field, index) => {
    if (isPublicFormContentItem(field)) return;
    const uid = `preview.field.${index}`;
    fieldIds.set(field.instance_id, uid);
    if (!fieldIds.has(field.field_key)) fieldIds.set(field.field_key, uid);
  });
  let repeatGroup = null;
  let previousStep = null;
  const items = draft.fields.map((field, index) => {
    if (previousStep !== field.step) repeatGroup = null;
    previousStep = field.step;
    if (isPublicFormContentItem(field)) {
      if (field.content_type === 'subtitle') {
        repeatGroup = field.repeat_group?.enabled ? {
          uid: `group.${index.toString(16).padStart(24, '0')}`,
          entry_label: field.repeat_group.entry_label,
          add_label: field.repeat_group.add_label,
          max_entries: 10,
        } : null;
      }
      return {
        kind: 'content', type: field.content_type, text: field.text,
        step: field.step, order: index,
        ...(field.content_type === 'subtitle' && repeatGroup ? { repeat_group: repeatGroup } : {}),
      };
    }
    const conditionUid = fieldIds.get(field.condition?.field_key);
    return {
      kind: 'field', uid: fieldIds.get(field.instance_id), label: field.label,
      type: field.type, help_text: field.help_text, placeholder: field.placeholder,
      options: field.options.map(({ value: optionValue, label }) => ({ value: optionValue, label })),
      required: field.required, repeatable: field.repeatable, step: field.step, order: index,
      ...(conditionUid ? { condition: { field_uid: conditionUid, operator: field.condition.operator, value: field.condition.value } } : {}),
      ...(repeatGroup ? { repeat_group_uid: repeatGroup.uid } : {}),
    };
  });
  return { title: draft.title || 'Untitled form preview', description: draft.description, items };
};

export const buildPublicFormPreviewDocument = (schema, renderer, css) => {
  // Fail closed against a stale static portal bundle, before running any script.
  if (!renderer.includes('previewVersion: 1')) throw new Error('Preview is not available yet. Please try again after the form assets are updated.');
  if (!globalThis.crypto || typeof globalThis.crypto.getRandomValues !== 'function') {
    throw new Error('This browser cannot open a secure preview. Try an up-to-date browser.');
  }
  const nonceBytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(nonceBytes);
  const nonce = Array.from(nonceBytes, (value) => value.toString(16).padStart(2, '0')).join('');
  const payload = JSON.stringify({ mode: 'local_preview_v1', schema }).replace(/[<>&\u2028\u2029]/g, (character) => (
    `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`
  ));
  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'nonce-${nonce}'; style-src 'unsafe-inline'; connect-src 'none'; img-src 'none'; font-src 'none'; base-uri 'none'; form-action 'none'; object-src 'none'; frame-src 'none'">
<meta name="referrer" content="no-referrer"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>Recipient form preview</title><style>${css.replace(/<\/style/gi, '<\\/style')}</style></head><body>
<div id="public-form-app" class="pf-app"><header class="pf-progress-shell"><div class="pf-progress-inner"><div class="pf-brand">Staff preview · No changes are saved</div><div class="pf-progress-heading"><h1 id="pf-progress-title">Loading preview…</h1><span id="pf-progress-count"></span></div><div id="pf-progress-track" class="pf-progress-track" role="progressbar" aria-label="Form progress" aria-valuemin="1" aria-valuemax="1" aria-valuenow="1"></div></div></header>
<main id="pf-main" class="pf-main" tabindex="-1"></main><footer id="pf-navigation" class="pf-navigation" hidden><div class="pf-navigation-inner"><button id="pf-back" class="pf-button pf-button-secondary" type="button">Back</button><button id="pf-next" class="pf-button pf-button-primary" type="button">Continue</button></div></footer></div>
<script nonce="${nonce}">window.__CASEDB_PUBLIC_FORM_PREVIEW__=${payload};</script>
<script nonce="${nonce}">${renderer.replace(/<\/script/gi, '<\\/script')}</script></body></html>`;
};

export const loadPublicFormPreviewDocument = async (schema, signal) => {
  const assets = await Promise.all(['/forms/public-form.js', '/forms/public-form.css'].map(async (path) => {
    const response = await fetch(path, {
      method: 'GET', credentials: 'omit', cache: 'no-store', redirect: 'error',
      referrerPolicy: 'no-referrer', signal,
    });
    if (!response.ok) throw new Error('Preview assets could not be loaded. Please try again.');
    const value = await response.text();
    if (!value || value.length > 262144) throw new Error('Preview assets are unavailable.');
    return value;
  }));
  return buildPublicFormPreviewDocument(schema, assets[0], assets[1]);
};
